import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DropdownPanelConfig } from '../../library/dropdown-panel/dropdown-panel.component';
import { ButtonSeverity } from 'primeng/types/button';
import { AvailableIntegrations } from '@/core/models/IntegrationEntity';
import { TranslateService } from '@ngx-translate/core';
import { IntegrationUtils } from '@/core/utils/integration-utils';

@Component({
    selector: 'app-export-actions-panel',
    templateUrl: './export-actions-panel.component.html',
    // styleUrls: ['./export-actions-panel.component.scss'],
    standalone: false,
})
export class ExportActionsPanelComponent implements OnInit, OnChanges {
    @Input() licenseStatePro: boolean = false;
    // Export handlers provided by the parent
    @Input() exportToPdf?: () => Promise<void>;
    @Input() exportToExcel?: () => Promise<void>;
    @Input() exportToCsv?: () => Promise<void>;
    @Input() accountingIntegrations: AvailableIntegrations[] = [];
    @Input() reauthIntegrations: AvailableIntegrations[] = [];
    @Input() rateLimitedIntegrations: AvailableIntegrations[] = [];
    @Input() isAdmin: boolean = true;
    @Input() selectedActivityIds: number[] | undefined = undefined;
    @Output() openAccountingModal = new EventEmitter<AvailableIntegrations>();
    isExporting: boolean = false;
    currentExport = '';
    topText: string | undefined = undefined;
    dropCfg: DropdownPanelConfig;

    constructor(private readonly translate: TranslateService) {
        this.dropCfg = this.getEmptyDropdownPanelConfig();
    }

    ngOnInit(): void {
        this.dropCfg = this.getDropdownPanelConfig();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // this is needed because the objects used in the template are only created once in ngOnInit(), in case of change they need to be updated.
        if (
            changes['licenseStatePro'] ||
            changes['exportToPdf'] ||
            changes['exportToExcel'] ||
            changes['exportToCsv'] ||
            changes['accountingIntegrations'] ||
            changes['reauthIntegrations'] ||
            changes['rateLimitedIntegrations'] ||
            changes['isAdmin'] ||
            changes['openAccountingModal']
        ) {
            this.update();
        }

        if (changes['selectedActivityIds'])
            if (this.selectedActivityIds) {
                this.topText = undefined; // 'export ' + this.selectedActivityIds.length + ' entries ';
            } else {
                this.topText = undefined;
            }
    }

    /**
     * Enforces an update of the component with current values.
     */
    update(): void {
        this.dropCfg = this.getDropdownPanelConfig();
    }

    getEmptyDropdownPanelConfig(): DropdownPanelConfig {
        return {
            labels: [],
            classes: [],
            severities: [],
            icons: [],
            tooltips: [],
            disabled: [],
            functions: [],
            showSpinner: [],
            showDividers: [],
            tooltipLinks: [],
            tooltipLinkLabels: [],
            tooltipAutoHide: [],
            buttonLabel: 'export_action',
            buttonIcon: 'pi pi-chevron-down',
        };
    }

    getDropdownPanelConfig(): DropdownPanelConfig {
        const cfg = this.getEmptyDropdownPanelConfig();
        const add = function (
            label: string,
            cls: string,
            severity: ButtonSeverity | undefined,
            icon: string,
            tooltip: string,
            disabled: boolean,
            fn: () => Promise<void>,
            showSpinner: boolean,
            showDivider: boolean = false,
            tooltipLink: string = '',
            tooltipLinkLabel: string = '',
        ): void {
            cfg.labels.push(label);
            cfg.classes.push(cls);
            cfg.severities.push(severity);
            cfg.icons.push(icon);
            cfg.tooltips.push(tooltip);
            cfg.disabled.push(disabled);
            cfg.functions.push(fn);
            cfg.showSpinner.push(showSpinner);
            cfg.showDividers.push(showDivider);
            cfg.tooltipLinks.push(tooltipLink);
            cfg.tooltipLinkLabels.push(tooltipLinkLabel);
            cfg.tooltipAutoHide.push(!tooltipLinkLabel);
        };

        for (let i = 0; i < this.accountingIntegrations.length; i++) {
            const serviceId = this.accountingIntegrations[i];
            const needsReauth = this.reauthIntegrations.includes(serviceId);
            const isRateLimited = this.rateLimitedIntegrations.includes(serviceId);
            const showDivider = i === this.accountingIntegrations.length - 1;
            const isDisabled = !this.licenseStatePro || needsReauth || isRateLimited;
            // Pro takes priority: if user is not Pro, always show the upgrade tooltip.
            // Only show the reauth tooltip when the user IS Pro but the token is invalid.

            // Note: tooltip is actually meant to be a localization key. In this case instead the already translated /
            //       localized value is assigned. This is actually not an issue because the translate pipe will return
            //       the value as is if it does not match any localization.
            //       Still this code should be optimized some time...
            //       ( => Major refactor of ExportActionsPanelComponent and DropdownPanelComponent)
            let tooltip = '';
            if (!this.licenseStatePro) {
                // TODO check if this pro tooltip can be removed, if the buttons are even shown when not pro
                tooltip = IntegrationUtils.transWithIntName(this.translate, serviceId, 'int_proFeature');
            } else if (needsReauth) {
                tooltip = IntegrationUtils.transWithIntName(
                    this.translate,
                    serviceId,
                    'int_invoice_reauth_required_tooltip',
                );
            } else if (isRateLimited) {
                // Rate limiting is a temporary condition, not a broken connection - do not offer a
                // reauth/settings link for it, just explain that it's temporary.
                tooltip = IntegrationUtils.transWithIntName(
                    this.translate,
                    serviceId,
                    'int_invoice_rate_limited_tooltip',
                );
            }

            const tooltipLink =
                this.licenseStatePro && needsReauth && this.isAdmin
                    ? IntegrationUtils.getDetailsPageLink(serviceId)
                    : '';
            const tooltipLinkLabel =
                this.licenseStatePro && needsReauth
                    ? this.isAdmin
                        ? 'int_open_settings'
                        : 'int_reauth_contact_admin'
                    : '';
            add(
                IntegrationUtils.getI18nPrefix(serviceId) + '.name',
                '',
                'danger',
                'pi pi-file',
                tooltip,
                isDisabled,
                () => {
                    this.onAccountingServiceClick(serviceId);
                    return Promise.resolve();
                },
                false,
                showDivider,
                tooltipLink,
                tooltipLinkLabel,
            );
        }
        if (this.exportToPdf) {
            add(
                'activities_pdf',
                'p-button-amber',
                undefined,
                'pi pi-file-pdf',
                'activities_pdf_proFeature',
                !this.licenseStatePro,
                this.clickExportToPdf,
                this.currentExport === 'pdf',
            );
        }
        if (this.exportToExcel) {
            add(
                'activities_xls',
                '',
                'success',
                'pi pi-file-excel',
                'activities_xls_proFeature',
                !this.licenseStatePro,
                this.clickExportToExcel,
                this.currentExport === 'xls',
            );
        }
        if (this.exportToCsv) {
            add(
                'activities_csv',
                '',
                'primary',
                'pi pi-file',
                'activities_csv_proFeature',
                !this.licenseStatePro,
                this.clickExportToCsv,
                this.currentExport === 'csv',
            );
        }
        if (this.isExporting) {
            cfg.disabled.fill(true);
            cfg.tooltips.fill('');
        }
        return cfg;
    }

    // TODO alter dropCfg to trigger state changes...

    private startExport(exportType: string): void {
        console.debug(`Starting export "${exportType}" at ` + Date.now());
        this.isExporting = true;
        this.currentExport = exportType;
        this.update();
    }

    private stopExport(exportType: string): void {
        console.debug(`Finished export "${exportType}" at ` + Date.now());
        this.isExporting = false;
        this.currentExport = '';
        this.update();
    }

    clickExportToPdf = async (): Promise<void> => {
        if (this.isExporting || !this.exportToPdf) return;
        this.startExport('pdf');
        try {
            await this.exportToPdf();
        } catch {}
        this.stopExport('pdf');
    };
    clickExportToExcel = async (): Promise<void> => {
        if (this.isExporting || !this.exportToExcel) return;
        this.startExport('xls');
        try {
            await this.exportToExcel();
        } catch {}
        this.stopExport('xls');
    };
    clickExportToCsv = async (): Promise<void> => {
        if (this.isExporting || !this.exportToCsv) return;
        this.startExport('csv');
        try {
            await this.exportToCsv();
        } catch (e) {
            console.error('Error while exporting to CSV', e);
        }
        this.stopExport('csv');
    };

    onAccountingServiceClick(serviceId: AvailableIntegrations): void {
        this.openAccountingModal.emit(serviceId);
    }
}
